import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { sendSmsTwoFaMutation, loginWithG2FaMutation, loginWithSmsTwoFaMutation, sendEmailVerificationCodeMutation, loginEmail2FAMutation, } from '@libs/graphql';
import { TwoFactorAuthType } from '@libs/types';
import { useApiResponse } from './useApiResponse';
import { useAuth } from './useAuth';
import { ErrorMessageTexts } from './constants';
export const useTwoFaDetection = (search) => {
    const { decodeToken } = useAuth();
    const decodedToken = useRef(decodeToken()).current;
    const detectTwoFaType = useCallback((decodedToken, filledCodes) => {
        if (decodedToken.isSmsTwoFactorEnabled &&
            (!decodedToken.isSmsSecondFactorAuthenticated ||
                !filledCodes?.twoFactorCode)) {
            return TwoFactorAuthType.SMS;
        }
        if (decodedToken.isEmailTwoFactorEnabled &&
            (!decodedToken.isEmailSecondFactorAuthenticated ||
                !filledCodes?.emailTwoFactorCode)) {
            return TwoFactorAuthType.EMAIL;
        }
        if (decodedToken.isTwoFactorEnabled &&
            (!decodedToken.isSecondFactorAuthenticated ||
                !filledCodes?.googleTwoFactorCode)) {
            return TwoFactorAuthType.GOOGLE;
        }
        return TwoFactorAuthType.SMS;
    }, []);
    const twoFAType = useMemo(() => {
        const params = new URLSearchParams(search);
        const type = params.get('type');
        if (!type && decodedToken) {
            return detectTwoFaType(decodedToken);
        }
        return type;
    }, [search, decodedToken, detectTwoFaType]);
    return {
        twoFAType,
        decodedToken,
        detectTwoFaType,
    };
};
export const useTwoFa = (type, onStepComplete, onAuthFinish, shouldSendSms = true, shouldSendEmail = true, shouldLogin = true) => {
    const twoFactorCodes = useRef({
        twoFactorCode: '',
        googleTwoFactorCode: '',
        emailTwoFactorCode: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [sendSms] = useMutation(sendSmsTwoFaMutation);
    const [sendEmail] = useMutation(sendEmailVerificationCodeMutation);
    const [smsTwoFaLogin, smsLoginRes] = useMutation(loginWithSmsTwoFaMutation);
    const [emailTwoFaLogin, emailLoginRes] = useMutation(loginEmail2FAMutation);
    const [g2FaLogin, googleLoginRes] = useMutation(loginWithG2FaMutation);
    const { detectTwoFaType } = useTwoFaDetection();
    const { checkTokenValidity, decodeToken, login, tokenKey } = useAuth();
    const setTwoFactorCode = (code, type) => {
        if (type === TwoFactorAuthType.SMS) {
            twoFactorCodes.current.twoFactorCode = code;
        }
        else if (type === TwoFactorAuthType.EMAIL) {
            twoFactorCodes.current.emailTwoFactorCode = code;
        }
        else if (type === TwoFactorAuthType.GOOGLE) {
            twoFactorCodes.current.googleTwoFactorCode = code;
        }
    };
    const handleTwoFaLogin = useCallback((type, code) => {
        const token = localStorage.getItem(tokenKey);
        setTwoFactorCode(code, type);
        if (!token)
            return;
        const args = {
            variables: {
                record: {
                    keepUser: false,
                    twoFactorCode: code,
                },
            },
        };
        if (type === TwoFactorAuthType.EMAIL) {
            emailTwoFaLogin(args);
        }
        else if (type === TwoFactorAuthType.SMS) {
            smsTwoFaLogin(args);
        }
        else if (type === TwoFactorAuthType.GOOGLE) {
            g2FaLogin(args);
        }
    }, [tokenKey, emailTwoFaLogin, smsTwoFaLogin, g2FaLogin]);
    const handleSuccessLogin = useCallback((res) => {
        if (!res)
            return;
        let accessToken = '';
        if ('loginSms2FA' in res) {
            accessToken = res.loginSms2FA.accessToken;
        }
        else if ('loginEmail2FA' in res) {
            accessToken = res.loginEmail2FA.accessToken;
        }
        else if ('loginGoogle2FA' in res) {
            accessToken = res.loginGoogle2FA.accessToken;
        }
        const isTokenFullyValid = checkTokenValidity(accessToken, type);
        const decodedToken = decodeToken(accessToken);
        setErrorMessage('');
        if (shouldLogin) {
            login(accessToken);
        }
        if (isTokenFullyValid) {
            onAuthFinish?.(accessToken, twoFactorCodes.current);
            return;
        }
        if (decodedToken) {
            onStepComplete?.(detectTwoFaType(decodedToken, twoFactorCodes.current));
        }
    }, [
        checkTokenValidity,
        type,
        decodeToken,
        shouldLogin,
        login,
        onAuthFinish,
        onStepComplete,
        detectTwoFaType,
    ]);
    const handleErrorMessage = (message) => {
        setErrorMessage(message);
    };
    const { loading: isSmsTwoFaLoginLoading } = useApiResponse(smsLoginRes, handleSuccessLogin, undefined, handleErrorMessage);
    const { loading: isEmailTwoFaLoginLoading } = useApiResponse(emailLoginRes, handleSuccessLogin, undefined, handleErrorMessage);
    const { loading: googleLoginLoading } = useApiResponse(googleLoginRes, handleSuccessLogin, undefined, handleErrorMessage);
    useEffect(() => {
        if (type === TwoFactorAuthType.SMS && shouldSendSms) {
            sendSms({
                variables: { record: {} },
            });
        }
        if (type === TwoFactorAuthType.EMAIL && shouldSendEmail) {
            sendEmail({
                variables: { record: {} },
            });
        }
    }, [sendEmail, sendSms, shouldSendEmail, shouldSendSms, type]);
    const sendCode = useCallback(() => {
        if (type === TwoFactorAuthType.SMS) {
            sendSms({
                variables: { record: {} },
            });
        }
        else if (type === TwoFactorAuthType.EMAIL) {
            sendEmail({
                variables: { record: {} },
            });
        }
    }, [sendEmail, sendSms, type]);
    return {
        isSmsTwoFaLoginLoading,
        isEmailTwoFaLoginLoading,
        isCodeInvalid: errorMessage === ErrorMessageTexts.INCORRECT_CODE,
        handleTwoFaLogin,
        sendCode,
        loading: googleLoginLoading || isEmailTwoFaLoginLoading || isSmsTwoFaLoginLoading,
        errorMessage,
    };
};
//# sourceMappingURL=useTwoFa.js.map